import { useEffect, useId, useRef, useState } from "react";
import type { PracticeClassification, PracticeKey } from "../../types";
import { normalizeReason } from "@/features/curator-queue/decision-intent";
import {
  MAX_PRACTICE_REASON_LENGTH,
  PRACTICE_KIND_LABELS,
  automatedPracticeKeys,
  practiceRowCopy,
  validateClearReason,
  type PracticeAssignmentCell,
  type PracticeAssignmentKind,
  type PracticeAttemptFailure,
} from "../../practice-assignment";
import { useCuratorPracticeAssignments } from "../../practice-assignment-hooks";

/**
 * Curator-only manual practice Include/Clear controls (3W), mounted in the
 * Settings administration surface beside the publication control and the
 * read-only practice panel.
 *
 * Nothing is read when the modal opens. get_signal_practices is requested only
 * after the curator opens "Manage practice assignments", and again after every
 * accepted decision; the rows shown are always that read's eight practices, in
 * the server's order, under the server's names. Settings stays open after
 * Include or Clear.
 *
 * Include and Clear govern only curator membership. If the detail read reports
 * automated routing for a practice, the row says so, and says that these
 * actions do not change it.
 */
export function CuratorPracticeControl({
  signalId,
  classification,
}: {
  signalId: string;
  /** The detail read's classification, used only to label automated routing. */
  classification: PracticeClassification | null | undefined;
}): JSX.Element {
  const { session, snapshot } = useCuratorPracticeAssignments(signalId);
  const baseId = useId();
  const headingId = `${baseId}-heading`;
  const panelId = `${baseId}-panel`;
  const panelRef = useRef<HTMLDivElement>(null);
  const actionRefs = useRef(new Map<PracticeKey, HTMLButtonElement>());

  const { expanded, read, readError, cells, confirm, pendingFor, failure, blockReason, notice } =
    snapshot;
  const reading = read === "reading";
  const conflicted = blockReason === "conflict";
  const automated = automatedPracticeKeys(classification);
  const rowsActionable = read === "ready" && pendingFor === null && !conflicted;

  // A state change can unmount the element that held focus - the confirm step
  // after a decision, or the rows while they are re-read. Focus then returns to
  // this panel instead of falling out of the Settings administration region.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const active = document.activeElement;
    if (active === null || active === document.body) {
      panel.focus({ preventScroll: true });
    }
  }, [snapshot]);

  const cancel = (practiceKey: PracticeKey) => {
    session.cancelConfirm();
    actionRefs.current.get(practiceKey)?.focus();
  };

  return (
    <section className="sx-z-practice-curation" aria-labelledby={headingId}>
      <p className="sx-zone-title sx-sm" id={headingId}>
        Practice assignments
      </p>
      <p className="sx-helper">
        Curator only. Include this signal in a practice, or clear a curator inclusion.
      </p>
      <button
        type="button"
        className="sx-btn sx-btn-p2"
        aria-expanded={expanded}
        aria-controls={panelId}
        // Closing forgets the in-flight decision's ownership, so it waits.
        disabled={pendingFor !== null}
        onClick={() => (expanded ? session.collapse() : session.expand())}
      >
        {expanded ? "Hide practice assignments" : "Manage practice assignments"}
      </button>

      {expanded ? (
        <div
          id={panelId}
          ref={panelRef}
          className="sx-practice-assign"
          tabIndex={-1}
          aria-busy={reading}
          aria-labelledby={headingId}
        >
          <p className="sx-helper" role="status">
            {reading ? "Reading current practice assignments…" : (notice ?? "")}
          </p>

          {read === "error" ? (
            <div className="sx-practice-assign-block">
              <p className="sx-popover-help sx-popover-help--error" role="alert">
                {readError}
              </p>
              <button type="button" className="sx-btn sx-btn-p3" onClick={() => session.refresh()}>
                {blockReason !== null ? "Refresh and start again" : "Try reading again"}
              </button>
            </div>
          ) : null}

          {conflicted && read !== "error" ? (
            <div className="sx-practice-assign-block">
              <p className="sx-popover-help sx-popover-help--error" role="alert">
                {failure?.message ?? "These practice assignments changed since they were read."}{" "}
                Refresh before deciding again.
              </p>
              <button
                type="button"
                className="sx-btn sx-btn-p3"
                disabled={reading}
                onClick={() => session.refresh()}
              >
                {reading ? "Refreshing…" : "Refresh and start again"}
              </button>
            </div>
          ) : null}

          {cells ? (
            <ul className="sx-practice-assign-list" aria-labelledby={headingId}>
              {cells.map((cell) => {
                const copy = practiceRowCopy(cell, automated.has(cell.practiceKey));
                const confirmOpen = confirm?.practiceKey === cell.practiceKey;
                const confirmId = `${baseId}-confirm-${cell.practiceKey}`;
                return (
                  <li key={cell.practiceKey} className="sx-practice-assign-row">
                    <div className="sx-practice-row">
                      <span className="sx-body sx-sm">{cell.displayName}</span>
                      <span className="sx-meta">{copy.stateLabel}</span>
                    </div>
                    {copy.automatedNote ? (
                      <p className="sx-practice-assign-note">{copy.automatedNote}</p>
                    ) : null}
                    <button
                      type="button"
                      className="sx-btn sx-btn-p3"
                      ref={(element) => {
                        if (element) actionRefs.current.set(cell.practiceKey, element);
                        else actionRefs.current.delete(cell.practiceKey);
                      }}
                      aria-haspopup="dialog"
                      aria-expanded={confirmOpen}
                      aria-controls={confirmOpen ? confirmId : undefined}
                      aria-label={`${copy.actionLabel}: ${cell.displayName}`}
                      disabled={!rowsActionable}
                      onClick={() =>
                        confirmOpen
                          ? cancel(cell.practiceKey)
                          : session.openConfirm(cell.practiceKey)
                      }
                    >
                      {copy.actionLabel}
                    </button>
                    {confirmOpen && confirm ? (
                      <PracticeDecisionConfirm
                        // A new row or decision starts with an empty reason.
                        key={`${cell.practiceKey}:${confirm.kind}`}
                        id={confirmId}
                        cell={cell}
                        kind={confirm.kind}
                        busy={pendingFor === cell.practiceKey}
                        disabled={pendingFor !== null || reading || conflicted}
                        failure={
                          failure?.practiceKey === cell.practiceKey && failure.kind === confirm.kind
                            ? failure
                            : null
                        }
                        nonRetryable={blockReason === "non_retryable"}
                        onCancel={() => cancel(cell.practiceKey)}
                        onConfirm={(reason) =>
                          session.submit(cell.practiceKey, confirm.kind, reason)
                        }
                        onRefresh={() => session.refresh()}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function PracticeDecisionConfirm({
  id,
  cell,
  kind,
  busy,
  disabled,
  failure,
  nonRetryable,
  onCancel,
  onConfirm,
  onRefresh,
}: {
  id: string;
  cell: PracticeAssignmentCell;
  kind: PracticeAssignmentKind;
  /** True while THIS row's decision is in flight. */
  busy: boolean;
  /** True while any decision or read is running, or a conflict stands. */
  disabled: boolean;
  /** The last failed attempt, when it was this row's and this decision. */
  failure: PracticeAttemptFailure | null;
  nonRetryable: boolean;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
  onRefresh: () => void;
}): JSX.Element {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const needsReason = kind === "clear";
  const verdict = validateClearReason(reason);
  const reasonProblem = needsReason ? verdict.problem : null;
  const showRequired = reasonProblem === "required" && touched;
  const showTooLong = reasonProblem === "too_long";
  // Resending the identical refused request would collect the identical answer.
  const refusedIdentical =
    nonRetryable &&
    failure !== null &&
    failure.failure === "non_retryable" &&
    failure.reason === (needsReason ? normalizeReason(reason) : null);
  const canConfirm = !busy && !disabled && reasonProblem === null && !refusedIdentical;
  const label = PRACTICE_KIND_LABELS[kind];
  const reasonId = `${id}-reason`;
  const errorId = `${id}-reason-error`;

  return (
    <div
      id={id}
      className="sx-popover sx-practice-confirm"
      role="dialog"
      aria-label={`${label}: ${cell.displayName}`}
    >
      <div className="sx-popover-title">
        {label} {cell.displayName}?
      </div>
      <p className="sx-popover-help">
        {kind === "include"
          ? `Records a curator inclusion of this signal in ${cell.displayName}.`
          : `Clears the curator inclusion of this signal in ${cell.displayName}. The decision and its reason are recorded permanently.`}
      </p>

      {needsReason ? (
        <>
          <label className="sx-eyebrow" htmlFor={reasonId}>
            Reason (required)
          </label>
          <textarea
            id={reasonId}
            className="sx-field"
            value={reason}
            disabled={busy}
            autoFocus
            aria-required={true}
            aria-invalid={showRequired || showTooLong}
            aria-describedby={showRequired || showTooLong ? errorId : undefined}
            placeholder="Why is this inclusion being cleared?"
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
          />
          {showTooLong ? (
            <p id={errorId} className="sx-popover-help sx-popover-help--error" role="alert">
              Reason must be {MAX_PRACTICE_REASON_LENGTH} characters or fewer.
            </p>
          ) : showRequired ? (
            <p id={errorId} className="sx-popover-help sx-popover-help--error" role="alert">
              A reason is required — whitespace alone is not a reason.
            </p>
          ) : null}
        </>
      ) : null}

      {failure && failure.failure !== "conflict" ? (
        <p className="sx-popover-help sx-popover-help--error" role="alert">
          {failure.message}
          {failure.failure === "retryable"
            ? " You can try again; the same decision is not applied twice."
            : " Sending this again would get the same answer. Refresh to start again, or change the decision."}
        </p>
      ) : null}

      <div className="sx-popover-actions">
        <button type="button" className="sx-btn sx-btn-p3" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        {refusedIdentical ? (
          <button type="button" className="sx-cta" disabled={busy} onClick={onRefresh}>
            Refresh and start again
          </button>
        ) : (
          <button
            type="button"
            className="sx-cta"
            disabled={!canConfirm}
            autoFocus={!needsReason}
            onClick={() => onConfirm(needsReason ? verdict.reason : null)}
          >
            {busy ? "Recording…" : label}
          </button>
        )}
      </div>
    </div>
  );
}
